import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../context/AuthContext";

/**
 * Heart / save button for businesses and services.
 * - Only customers can favorite. For everyone else the heart is hidden.
 * - Reads initial state from /api/favorites/ids (cached on window for the session).
 * - Optimistic UI: flips instantly, reverts on error.
 *
 * Props:
 *   targetType: "business" | "service"
 *   targetId:   string
 *   variant:    "card" (absolutely-positioned corner button, default) | "inline" (button in a row)
 *   size:       number (icon size px, default 18)
 *   onChange:   (favorited: boolean) => void  optional
 */

// Per-session cache so hearts across many cards don't each hit the API on mount.
let _favCache = null; // { businesses:Set, services:Set }
const _cacheListeners = new Set();

async function loadFavIds(authHeader) {
  try {
    const r = await axios.get(`${API}/favorites/ids`, { headers: authHeader() });
    _favCache = {
      businesses: new Set(r.data.business_ids || []),
      services: new Set(r.data.service_ids || []),
    };
    _cacheListeners.forEach((fn) => fn());
  } catch {
    _favCache = { businesses: new Set(), services: new Set() };
  }
}

export function invalidateFavoritesCache() {
  _favCache = null;
  _cacheListeners.forEach((fn) => fn());
}

export default function FavoriteButton({ targetType, targetId, variant = "card", size = 18, onChange }) {
  const { user, authHeader } = useAuth();
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const enabled = user && user.role === "customer";

  useEffect(() => {
    if (!enabled) return;
    const sync = () => {
      if (!_favCache) return;
      const set = targetType === "business" ? _favCache.businesses : _favCache.services;
      setSaved(set.has(targetId));
    };
    _cacheListeners.add(sync);
    if (!_favCache) loadFavIds(authHeader);
    else sync();
    return () => { _cacheListeners.delete(sync); };
  }, [enabled, authHeader, targetType, targetId]);

  if (!enabled) return null;

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    const next = !saved;
    setSaved(next);
    try {
      if (next) {
        await axios.post(`${API}/favorites/${targetType}/${targetId}`, {}, { headers: authHeader() });
        if (_favCache) (targetType === "business" ? _favCache.businesses : _favCache.services).add(targetId);
        toast.success("Saved to favorites");
      } else {
        await axios.delete(`${API}/favorites/${targetType}/${targetId}`, { headers: authHeader() });
        if (_favCache) (targetType === "business" ? _favCache.businesses : _favCache.services).delete(targetId);
        toast.success("Removed from favorites");
      }
      onChange && onChange(next);
    } catch (err) {
      setSaved(!next);
      toast.error(err?.response?.data?.detail || "Could not update favorites");
    } finally {
      setBusy(false);
    }
  };

  const iconStyle = { transition: "transform 150ms ease", transform: busy ? "scale(0.9)" : saved ? "scale(1.05)" : "scale(1)" };
  const Heart = (
    <svg width={size} height={size} viewBox="0 0 24 24"
      fill={saved ? "var(--gf-accent)" : "none"}
      stroke={saved ? "var(--gf-accent)" : "currentColor"}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );

  if (variant === "inline") {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        aria-pressed={saved}
        aria-label={saved ? "Remove from favorites" : "Save to favorites"}
        data-testid={`fav-${targetType}-${targetId}`}
        className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs hover:opacity-90 disabled:opacity-60"
        style={{ borderColor: "var(--gf-border-strong)", color: saved ? "var(--gf-accent)" : "var(--gf-text)", background: "transparent" }}
      >
        {Heart}
        <span>{saved ? "Saved" : "Save"}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-pressed={saved}
      aria-label={saved ? "Remove from favorites" : "Save to favorites"}
      data-testid={`fav-${targetType}-${targetId}`}
      className="absolute top-3 right-3 z-10 grid place-items-center rounded-full w-9 h-9 shadow-sm hover:scale-105 transition-transform disabled:opacity-60"
      style={{ background: "rgba(255,255,255,0.95)", color: "var(--gf-text)", backdropFilter: "blur(2px)" }}
    >
      {Heart}
    </button>
  );
}
